// The quote's content-intake state (ADR-021 §7): one entry per media line with
// its latest upload session and latest link check. Read by the pricing
// pre-flight (QuoteEdit) and by the uploader and link-check controls, which all
// share this one query by key.

import { useQuery, type QueryClient } from '@tanstack/react-query';

import {
  mediaContentLineReady,
  readMediaContentDelivery,
  readMediaContentLink,
  type MediaContentLineState,
  type MediaContentQuoteStateResponse,
} from 'shared';

import * as mediaContentApi from '../api/media-content.js';

const INGEST_POLL_MS = 3000;

export function mediaContentStateKey(quote_id: string): readonly unknown[] {
  return ['media-content-state', quote_id];
}

function isIngesting(data: MediaContentQuoteStateResponse | undefined): boolean {
  return (data?.lines ?? []).some((l) => {
    const s = l.latest_link_check?.ingest_status;
    return l.latest_link_check?.status === 'ok' && (s === 'pending' || s === 'ingesting');
  });
}

export function useMediaContentQuoteState(quote_id: string | undefined) {
  return useQuery({
    queryKey: mediaContentStateKey(quote_id ?? ''),
    queryFn: () => mediaContentApi.getQuoteMediaContentState(quote_id!),
    enabled: Boolean(quote_id),
    refetchInterval: (query) => (isIngesting(query.state.data) ? INGEST_POLL_MS : false),
  });
}

export function invalidateMediaContentState(qc: QueryClient, quote_id: string): Promise<void> {
  return qc.invalidateQueries({ queryKey: mediaContentStateKey(quote_id) });
}

export function mediaContentLineState(
  data: MediaContentQuoteStateResponse | undefined,
  quote_line_id: string
): MediaContentLineState | null {
  return data?.lines.find((l) => l.quote_line_id === quote_line_id) ?? null;
}

// The `mediaContent` argument of lineMissingUploadFieldKeys for one line, judged
// by the shared mediaContentLineReady the backend price and submit gates use.
export function mediaContentReadiness(
  config: unknown,
  line: MediaContentLineState | null
): { delivery: string | null; ready: boolean } {
  const delivery = readMediaContentDelivery(config) ?? null;
  return {
    delivery,
    ready: mediaContentLineReady({
      delivery,
      configured_link: readMediaContentLink(config),
      latest_session: line?.latest_session ?? null,
      latest_link_check: line?.latest_link_check ?? null,
    }),
  };
}
